/* eslint-env browser */
// Cars (wave 3, W2_Portal_Tables look): every car in the modern table with
// its driver, the approval switch, the picture (click to change), Edit in
// each row and Delete in the row menu; "Add car" and Refresh at the top.
//
// Data and writes are the old page's, unchanged:
// - the /cars list is fetched once per mount only when this session has no
//   fresh copy (utils/carsSession), and again on Refresh;
// - each row action first reads that one car's current server copy (~1 KB)
//   and writes from it (a car deleted elsewhere is dropped, never re-created);
// - approve: updateUserCar(driver, { carApproved }) then
//   editCar({ ...car, id, approved }, 'Update') (admin, carApproval, critical);
// - picture: editCar({ ...car, id, car_image: file }, 'UpdateImage');
// - delete (admin, critical, not the driver's active car): editCar(car, 'Delete').
// material-table's tableData and the joined driverName are never written.
import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { get } from 'firebase/database';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { api, FirebaseContext } from 'common';
import { getLangKey } from 'common/src/other/getLangKey';
import { carsFresh, loadCars, markCarsStale } from '../utils/carsSession';
import { showToast, confirmDialog, InlineMessage } from '../components/feedback';
import { DataTable, PageHeader, YesNo, calmCaps, usePageState } from '../components/modern';
import { tokens as c } from '../theme';

export default function CarsList() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const settings = useSelector((state) => state.settingsdata.settings);
  const userdata = useSelector((state) => state.usersdata);
  const auth = useSelector((state) => state.auth);
  const { updateUserCar, editCar, fetchCars } = api;
  const { carEditRef } = useContext(FirebaseContext);
  const [driversObj, setDriversObj] = useState();
  const [data, setData] = useState([]);
  const carlistdata = useSelector((state) => state.carlistdata);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [role, setRole] = useState(null);
  const { state } = useLocation();
  const critical = !!settings.AllowCriticalEditsAdmin;
  const isAdmin = auth.profile.usertype === 'admin';

  // The page and the table's search are kept while staff open a record and
  // come back (owner, 28 Oct); components/modern/pageState.js.
  const [page, setPage] = usePageState('cars', 'page', Number.isInteger(state?.pageNo) && state.pageNo >= 0 ? state.pageNo : 0);
  useEffect(() => {
    if (Number.isInteger(state?.pageNo) && state.pageNo >= 0) setPage(state.pageNo);
  }, [state]);

  // Rows this mount changed in place: id -> patch, or null once deleted.
  // EDIT_CAR is a no-op reducer, so the slice stays stale after our own edit;
  // the overlay is re-applied every time `data` is re-derived below (a users
  // cache patch rebuilds driversObj, which used to wipe the local change).
  const localEdits = useRef({});

  // Cars are no longer pre-fetched at login for staff roles. Fetch ONCE per
  // mount, and only when this session has no fresh full list (5.8 MB read).
  // The guard is a const evaluated outside the effect and the deps are empty on
  // purpose: FETCH_CARS replaces the slice object before the data lands, so an
  // effect depending on the slice re-dispatches on every render.
  const carsFreshAtMount = carsFresh(carlistdata, auth.profile && auth.profile.uid);
  useEffect(() => {
    if (!carsFreshAtMount) loadCars(dispatch, fetchCars, auth.profile.uid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Manual refresh: other staff / mobile drivers change /cars too, and those
  // edits only reach this session through a refetch.
  const handleRefreshCars = () => {
    localEdits.current = {};
    markCarsStale();
    loadCars(dispatch, fetchCars, auth.profile.uid);
  };

  useEffect(() => {
    if (role !== 'driver' && userdata.users) {
      let arr = userdata.users.filter(
        (user) =>
          user.usertype === 'driver' &&
          ((role === 'fleetadmin' && user.fleetadmin && user.fleetadmin === auth.profile.uid) ||
            role === 'admin' ||
            role === 'finance' ||
            role === 'callcenteragent' ||
            role === 'callcentersupervisor')
      );
      let obj = {};
      for (let i = 0; i < arr.length; i++) {
        let user = arr[i];
        obj[user.id] = `${user.firstName} ${user.lastName} (${settings.AllowCriticalEditsAdmin ? user.mobile : t('hidden_demo')}) ${
          settings.AllowCriticalEditsAdmin ? user.email : t('hidden_demo')
        }`;
      }
      setDriversObj(obj);
    }
  }, [userdata.users, settings.AllowCriticalEditsAdmin, role, auth.profile.uid, t]);

  useEffect(() => {
    if (auth.profile && auth.profile.usertype) {
      setRole(auth.profile.usertype);
    }
  }, [auth.profile]);

  useEffect(() => {
    if (carlistdata?.cars && driversObj) {
      const edits = localEdits.current;
      const updatedData = carlistdata.cars
        .filter((car) => edits[car?.id] !== null)
        .map((car) => ({
          ...car,
          ...edits[car?.id],
          driverName: driversObj[car?.driver] || ' ',
        }));
      setData(updatedData);
    } else {
      setData([]);
    }
  }, [carlistdata?.cars, driversObj]);

  // The list is loaded once per session, so a row can be hours old — and the
  // row actions below write the WHOLE car record (set) or decide from it.
  // Each action therefore starts from ONE read of the car's current server
  // copy (~1 KB, no list refetch). ok:false = the read itself failed; the
  // caller then falls back to the row, as before.
  const readFreshCar = async (carId) => {
    try {
      const snap = await get(carEditRef(carId));
      return { ok: true, car: snap.val() };
    } catch (_) {
      return { ok: false, car: null };
    }
  };

  // material-table's tableData and the joined driverName are UI-only: they
  // must never be written into /cars.
  const toCarRecord = ({ tableData, driverName, ...car }) => car;

  // The car was deleted elsewhere since the list was loaded: writing the row
  // back would re-create it. Drop it from this view instead.
  const dropMissingCar = (carId) => {
    localEdits.current[carId] = null;
    markCarsStale();
    setData((prev) => prev.filter((car) => car.id !== carId));
    showToast.warning(t('car_no_longer_exists', { defaultValue: 'This car no longer exists. It has been removed from the list.' }));
  };

  // ── Picture ───────────────────────────────────────────────────────────
  const canChangeImage = isAdmin || role === 'callcenteragent' || role === 'callcentersupervisor';
  const [imageFor, setImageFor] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageBusy, setImageBusy] = useState(false);
  const [imageError, setImageError] = useState(null);
  const previewUrl = useMemo(() => (selectedImage ? URL.createObjectURL(selectedImage) : null), [selectedImage]);
  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl]
  );
  const openImage = (row) => {
    if (!canChangeImage) return;
    setImageFor(row);
    setSelectedImage(null);
    setImageError(null);
  };
  const closeImage = () => {
    if (imageBusy) return;
    setImageFor(null);
    setSelectedImage(null);
    setImageError(null);
  };

  const handleSaveImage = async () => {
    if (!settings.AllowCriticalEditsAdmin) {
      showToast.warning(t('demo_mode'));
      return;
    }
    if (!selectedImage) {
      setImageError(t('choose_image_first'));
      return;
    }
    const userData = imageFor;
    setImageBusy(true);
    setImageError(null);
    const { ok, car: fresh } = await readFreshCar(userData.id);
    if (ok && !fresh) {
      dropMissingCar(userData.id);
      setImageBusy(false);
      setImageFor(null);
      setSelectedImage(null);
      return;
    }
    let finalData = { ...toCarRecord(ok ? fresh : userData), id: userData.id };
    finalData.car_image = selectedImage;
    // Stale now and again once the upload has landed, so the next list
    // mount refetches and shows the new image.
    markCarsStale();
    try {
      await Promise.resolve(dispatch(editCar(finalData, 'UpdateImage')));
    } catch (e) {
      markCarsStale();
      setImageBusy(false);
      setImageError(e && e.message ? e.message : t('form_save_failed', { defaultValue: 'Could not save' }));
      return;
    }
    // The thunk writes the download URL back onto the object it was given.
    // EDIT_CAR is a no-op reducer, so show it through the overlay (the
    // re-derive after the users-cache patch would restore the old image).
    const url = finalData.car_image;
    if (typeof url === 'string') {
      localEdits.current[finalData.id] = { ...localEdits.current[finalData.id], car_image: url };
      setData((prev) => prev.map((car) => (car.id === finalData.id ? { ...car, car_image: url } : car)));
    }
    markCarsStale();
    setImageBusy(false);
    setImageFor(null);
    setSelectedImage(null);
    showToast.success(t('car_types_image_saved', { defaultValue: 'Picture saved' }));
  };

  // ── Approval ──────────────────────────────────────────────────────────
  const approving = useRef({}); // car id -> a fresh read is in flight
  const handelApproved = async (rowData) => {
    if (settings.carApproval && role === 'admin') {
      if (approving.current[rowData.id]) return;
      approving.current[rowData.id] = true;
      // What the staff member asked for: the opposite of the Switch they
      // clicked. (Toggling the server value instead would UN-approve a car
      // another admin approved since this list was loaded.)
      const approved = !rowData.approved;
      const { ok, car: fresh } = await readFreshCar(rowData.id);
      delete approving.current[rowData.id];
      if (ok && !fresh) {
        dropMissingCar(rowData.id);
        return;
      }
      // Every other field comes from the car's current server copy.
      const car = toCarRecord(ok ? fresh : rowData);
      dispatch(
        updateUserCar(car.driver, {
          carApproved: approved,
        })
      );

      dispatch(editCar({ ...car, id: rowData.id, approved }, 'Update'));
      // EDIT_CAR is a no-op reducer, so the approval Switch wouldn't flip until
      // a refetch. Reflect it in the list locally (no extra RTDB read).
      localEdits.current[rowData.id] = { ...localEdits.current[rowData.id], approved };
      markCarsStale();
      setData((prev) => prev.map((car2) => (car2.id === rowData.id ? { ...car2, approved } : car2)));
    }
  };

  // ── Delete (admin) ────────────────────────────────────────────────────
  const handleDelete = async (row) => {
    if (!settings.AllowCriticalEditsAdmin) {
      showToast.warning(t('demo_mode'));
      return;
    }
    const ok = await confirmDialog({
      title: t('cars_delete_title', { plate: row.vehicleNumber || '', defaultValue: 'Delete the car {{plate}}?' }),
      body: t('cars_delete_body', { defaultValue: 'It is removed from the driver’s cars. This cannot be undone.' }),
      confirmLabel: t('delete', { defaultValue: 'Delete' }),
      cancelLabel: t('confirm_cancel', { defaultValue: 'Cancel' }),
      destructive: true,
    });
    if (!ok) return;
    // Guard on the car's CURRENT active flag: the row can be hours old and
    // the car may have become the driver's active car since.
    const { ok: readOk, car: fresh } = await readFreshCar(row.id);
    if (readOk && !fresh) {
      dropMissingCar(row.id);
    } else if (readOk ? fresh.active : row.active) {
      showToast.error(t('active_car_delete'));
    } else {
      dispatch(editCar(toCarRecord(row), 'Delete'));
      // Drop the row locally (no 5.8 MB refetch, keeps the page); the next
      // list mount refetches once.
      localEdits.current[row.id] = null;
      markCarsStale();
      setData((prev) => prev.filter((car) => car.id !== row.id));
      showToast.success(t('cars_deleted', { defaultValue: 'Car deleted' }));
    }
  };

  // ── Table ─────────────────────────────────────────────────────────────
  const allowPrivate = !!settings.AllowCriticalEditsAdmin;
  const carTypeText = (row) => (row.carType ? t(getLangKey(row.carType), { defaultValue: row.carType }) : '');
  const approveEnabled = !!(settings.AllowCriticalEditsAdmin && settings.carApproval && role === 'admin');
  const columns = [
    { title: t('createdAt'), field: 'createdAt', type: 'datetime', defaultSort: 'desc' },
    {
      title: t('driver'),
      field: 'driverName',
      hidden: role === 'driver',
      minWidth: 200,
      render: (row) => (row.driverName && row.driverName.trim() ? row.driverName : null),
      exportValue: (row) => row.driverName,
    },
    { title: t('car_type'), field: 'carType', searchValue: carTypeText, sortValue: carTypeText, exportValue: carTypeText, render: carTypeText },
    {
      title: t('vehicle_reg_no'),
      field: 'vehicleNumber',
      nowrap: true,
      render: (row) => (row.vehicleNumber ? <Box component="span" sx={{ fontWeight: 600 }}>{row.vehicleNumber}</Box> : null),
    },
    { title: t('vehicle_model_name'), field: 'vehicleMake' },
    { title: t('vehicle_model_no'), field: 'vehicleModel' },
    { title: t('other_info'), field: 'other_info' },
    {
      title: t('image'),
      field: 'car_image',
      sortable: false,
      searchable: false,
      print: false,
      render: (row) => {
        if (!row.car_image || typeof row.car_image !== 'string') return null;
        const img = (
          <Box
            component="img"
            alt={canChangeImage ? '' : t('car_image')}
            src={row.car_image}
            sx={{ width: 56, height: 40, objectFit: 'cover', display: 'block', borderRadius: '8px', bgcolor: c.SURFACE_ALT }}
          />
        );
        return canChangeImage ? (
          <ButtonBase
            onClick={() => openImage(row)}
            aria-label={`${t('upload_car_image')}: ${row.vehicleNumber || ''}`}
            sx={{ borderRadius: '8px', '&.Mui-focusVisible': { outline: `2px solid ${c.ACCENT}`, outlineOffset: 2 } }}
          >
            {img}
          </ButtonBase>
        ) : (
          img
        );
      },
    },
    { title: t('active_status'), field: 'active', type: 'boolean' },
    {
      title: t('approved'),
      field: 'approved',
      type: 'boolean',
      render: (row) =>
        role === 'admin' && settings.carApproval ? (
          <Switch
            checked={!!row.approved}
            onChange={() => handelApproved(row)}
            disabled={!approveEnabled}
            inputProps={{ 'aria-label': `${t('approved')}: ${row.vehicleNumber || ''}` }}
          />
        ) : (
          <YesNo value={row.approved} yes={t('yes')} no={t('no')} />
        ),
    },
  ];

  const navState = { state: { pageNo: page } };
  const goAdd = () => navigate('/cars/editcar', navState);
  const goEdit = (row) => navigate(`/cars/editcar/${row.id}`, navState);
  const listLoading = !!carlistdata.loading;
  // Add was open to admin and call centre on a list with cars, and to every
  // role on an empty list (as before).
  const canAdd = isAdmin || role === 'callcenteragent' || role === 'callcentersupervisor' || (!listLoading && data.length === 0);

  const actions = [
    ...(isAdmin ? [{ key: 'edit', label: t('edit'), icon: <EditOutlinedIcon />, inline: true, onClick: goEdit }] : []),
    ...(canChangeImage ? [{ key: 'image', label: t('upload_car_image'), icon: <AddPhotoAlternateOutlinedIcon />, onClick: openImage }] : []),
    ...(isAdmin
      ? [{ key: 'delete', label: t('cars_delete', { defaultValue: 'Delete car' }), icon: <DeleteOutlineRoundedIcon />, destructive: true, onClick: handleDelete }]
      : []),
  ];

  const title = calmCaps(t('cars_title'));
  const addLabel = calmCaps(t('add_car'));

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <PageHeader
        secondaryActions={[
          {
            key: 'refresh',
            label: t('refresh', { defaultValue: 'Refresh' }),
            icon: listLoading ? <CircularProgress size={16} color="inherit" /> : <RefreshRoundedIcon />,
            onClick: handleRefreshCars,
            disabled: listLoading,
          },
        ]}
        primaryAction={{ label: addLabel, icon: <AddRoundedIcon />, onClick: goAdd, show: canAdd }}
      />
      <DataTable
        title={title}
        rows={data}
        columns={columns}
        rowKey="id"
        loading={listLoading && !data.length}
        refreshing={listLoading && data.length > 0}
        countText={({ shown, total, query, loading }) =>
          loading
            ? t('loading', { defaultValue: 'Loading…' })
            : query
              ? t('table_count_shown', { shown, total, defaultValue: '{{shown}} of {{total}}' })
              : t('cars_count', { count: total, defaultValue_one: '{{count}} car', defaultValue_other: '{{count}} cars', defaultValue: '{{count}} cars' })
        }
        rowLabel={(row) => row.vehicleNumber || carTypeText(row)}
        actions={actions}
        searchPlaceholder={t('cars_search', { defaultValue: 'Search plate, driver or model' })}
        searchFn={(row, q, ql) =>
          [row.vehicleNumber, row.vehicleMake, row.vehicleModel, row.other_info, carTypeText(row), role === 'driver' ? '' : row.driverName].some((v) =>
            String(v || '').toLowerCase().includes(ql)
          )
        }
        resortKey={allowPrivate}
        page={page}
        onPageChange={setPage}
        prefsKey="cars"
        stateKey="cars"
        exportCsv={isAdmin && critical}
        exportPrint={isAdmin}
        exportFileName="cars"
        exportTitle={title}
        minWidth={1240}
        emptyState={({ query }) =>
          query
            ? null
            : {
                icon: <DirectionsCarOutlinedIcon />,
                title: t('cars_empty', { defaultValue: 'No cars yet' }),
                actionLabel: canAdd ? addLabel : undefined,
                onAction: canAdd ? goAdd : undefined,
              }
        }
      />

      <Dialog open={!!imageFor} onClose={closeImage} fullWidth maxWidth="xs" fullScreen={phone} aria-labelledby="car-image-title">
        <DialogTitle id="car-image-title" sx={{ pr: 8 }}>
          {t('upload_car_image')}
          {imageFor && imageFor.vehicleNumber ? (
            <Typography component="span" sx={{ display: 'block', fontSize: 14, color: c.TEXT_3, fontWeight: 400 }}>
              {imageFor.vehicleNumber}
            </Typography>
          ) : null}
          <IconButton
            aria-label={t('close_dialog', { defaultValue: 'Close' })}
            onClick={closeImage}
            disabled={imageBusy}
            sx={{ position: 'absolute', right: 12, top: 12 }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {imageError ? (
            <InlineMessage kind="error" sx={{ mb: 2 }}>
              {imageError}
            </InlineMessage>
          ) : null}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, pt: 0.5 }}>
            {previewUrl || (imageFor && typeof imageFor.car_image === 'string' && imageFor.car_image) ? (
              <Box
                component="img"
                alt={t('car_image')}
                src={previewUrl || imageFor.car_image}
                sx={{ width: 200, height: 200, objectFit: 'contain', borderRadius: '12px', border: `1px solid ${c.OUTLINE}`, bgcolor: c.SURFACE_ALT }}
              />
            ) : null}
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center', gap: 1 }}>
              <Button variant="outlined" color="neutral" component="label" startIcon={<UploadFileOutlinedIcon />} disabled={imageBusy}>
                {selectedImage
                  ? t('car_types_image_other', { defaultValue: 'Choose another picture' })
                  : t('car_types_image_choose', { defaultValue: 'Choose a picture' })}
                <input
                  hidden
                  type="file"
                  accept="image/*"
                  name={t('image')}
                  onChange={(event) => {
                    const file = event.target.files && event.target.files[0];
                    if (file) {
                      setSelectedImage(file);
                      setImageError(null);
                    }
                    event.target.value = '';
                  }}
                />
              </Button>
              {selectedImage ? (
                <Button variant="text" color="neutral" onClick={() => setSelectedImage(null)} disabled={imageBusy}>
                  {t('car_types_image_remove', { defaultValue: 'Remove' })}
                </Button>
              ) : null}
            </Box>
            {selectedImage ? (
              <Typography sx={{ fontSize: 13.5, color: c.TEXT_3, overflowWrap: 'anywhere', textAlign: 'center' }}>{selectedImage.name}</Typography>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="text" color="neutral" onClick={closeImage} disabled={imageBusy}>
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveImage}
            disabled={imageBusy}
            startIcon={imageBusy ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {imageBusy ? t('form_saving', { defaultValue: 'Saving…' }) : t('save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
